import { useSelector } from "react-redux";

import { ALL_CONFIG_VERTICALS } from "../../../config/constants";
import {
	ACCOUNTS_PAYABLE_PATH,
	BACKGROUND_SEARCH_PATH,
	BUSINESS_PASSWORD_MANAGERS_PATH,
	BUSINESS_VPN_PATH,
	CELL_PHONE_PLANS_PATH,
	COFFEE_CLUBS_PATH,
	DIET_MEAL_PLANS_PATH,
	DNA_TESTING_PATH,
	ELECTRIC_BIKES_PATH,
	ELECTRONIC_SIGNATURE_PATH,
	FLOWER_DELIVERY_PATH,
	GLOBAL_HR_PATH,
	HAIR_GROWTH_PATH,
	INCORPORATION_SERVICES_PATH,
	INVISIBLE_BRACES_PATH,
	LANGUAGE_LEARNING_PATH,
	MATTRESS_TOPPERS_PATH,
	MATTRESSES_PATH,
	MEAL_DELIVERY_PATH,
	MEAT_DELIVERY_SERVICES_PATH,
	MUSHROOM_COFFEE_PATH,
	PDF_EDITORS_PATH,
	PERSONAL_VPN_PATH,
	PET_FOOD_DELIVERY_PATH,
	PROMOTIONAL_PRODUCTS_PATH,
	SOLAR_PATH,
	SURVEY_SITES_PATH,
	TEA_CLUBS_PATH,
	TEETH_WHITENING_PATH,
	WEBSITE_BUILDERS_PATH,
	WEIGHT_LOSS_PATH,
	WINE_CLUBS_PATH,
} from "../../../config/site/favy";
import { BaseTransitionWrapper } from "../../components/BaseComponents/BaseTransitionWrapper";
import { BaseVerticalCheck } from "../../components/BaseComponents/BaseVerticalCheck";
import { HairGrowthFilter } from "../../components/Filtering/HairGrowthFilter";
import { AppProviders } from "../../components/UIComponents/AppProviders";
import { BaseCompanyList } from "../../components/UIComponents/BaseCompanyList";
import { GridLayoutBeta } from "../../components/Verticals/GridLayoutBeta/GridLayoutBeta";
import { logError } from "../../helpers/logError";
import { getIsAmazonVertical, getVertical } from "../../selectors";
import { BackgroundSearchWidget } from "./components/BackgroundSearchWidget";
import { CompanyListHeader } from "./components/CompanyListHeader";
import { SaleHighlight } from "./components/SaleHighlight";
import { AmazonProduct } from "./verticals/Amazon/AmazonProduct";
import { FavyColumns } from "./verticals/FavyColumns/FavyColumns";
import { FavyColumnsCoupon } from "./verticals/FavyColumns/FavyColumnsCoupon";
import { FavyColumnsPhone } from "./verticals/FavyColumns/FavyColumnsPhone";
import { FavyColumnsTrustpilot } from "./verticals/FavyColumns/FavyColumnsTrustpilot";
import { FavyColumnsWebWidget } from "./verticals/FavyColumns/FavyColumnsWebWidget";
import { FavySoftwareLayout } from "./verticals/FavySoftware/FavySoftwareLayout";
import { FavySoftwareTrustpilot } from "./verticals/FavySoftware/FavySoftwareTrustpilot";
import { FavySoftwareWebWidget } from "./verticals/FavySoftware/FavySoftwareWebWidget";
import { IncorporationServicesCompany } from "./verticals/IncorporationServices/IncorporationServicesCompany";
import { IncorporationServicesFoldPlacements } from "./verticals/IncorporationServices/IncorporationServicesFoldPlacements";
import { styles } from "./verticals/pageLayoutStyles";
import { WeightLossCompany } from "./verticals/WeightLoss/WeightLossCompany";

const FavyCompany = () => {
	const vertical = useSelector(getVertical);
	const isAmazonVertical = useSelector(getIsAmazonVertical);

	if (isAmazonVertical) {
		return <AmazonProduct />;
	}

	// if 'vertical' is not registered amongst the keys of all the site configs, then render fallback layout
	if (!ALL_CONFIG_VERTICALS.includes(vertical)) {
		return <GridLayoutBeta />;
	}

	const componentMap = {
		// http://local.favy.com/accounts-payable
		[ACCOUNTS_PAYABLE_PATH]: FavySoftwareLayout,
		// http://local.favy.com/background-search
		[BACKGROUND_SEARCH_PATH]: GridLayoutBeta,
		// http://local.favy.com/business-password-managers
		[BUSINESS_PASSWORD_MANAGERS_PATH]: FavySoftwareTrustpilot,
		// http://local.favy.com/business-vpn
		[BUSINESS_VPN_PATH]: FavySoftwareWebWidget,
		// http://local.favy.com/cell-phone-plans
		[CELL_PHONE_PLANS_PATH]: FavyColumnsCoupon,
		// http://local.favy.com/coffee-clubs
		[COFFEE_CLUBS_PATH]: GridLayoutBeta,
		// http://local.favy.com/diet-meal-plans
		[DIET_MEAL_PLANS_PATH]: GridLayoutBeta,
		// http://local.favy.com/dna-testing
		[DNA_TESTING_PATH]: GridLayoutBeta,
		// http://local.favy.com/electric-bikes
		[ELECTRIC_BIKES_PATH]: GridLayoutBeta,
		// http://local.favy.com/electronic-signature
		[ELECTRONIC_SIGNATURE_PATH]: FavySoftwareLayout,
		// http://local.favy.com/flower-delivery
		[FLOWER_DELIVERY_PATH]: GridLayoutBeta,
		// http://local.favy.com/global-hr
		[GLOBAL_HR_PATH]: FavySoftwareTrustpilot,
		// http://local.favy.com/hair-growth
		[HAIR_GROWTH_PATH]: GridLayoutBeta,
		// http://local.favy.com/incorporation-services
		[INCORPORATION_SERVICES_PATH]: IncorporationServicesCompany,
		// http://local.favy.com/invisible-braces
		[INVISIBLE_BRACES_PATH]: GridLayoutBeta,
		// http://local.favy.com/language-learning
		[LANGUAGE_LEARNING_PATH]: GridLayoutBeta,
		// http://local.favy.com/mattress-toppers
		[MATTRESS_TOPPERS_PATH]: FavyColumns,
		// http://local.favy.com/mattresses
		[MATTRESSES_PATH]: FavyColumnsTrustpilot,
		// http://local.favy.com/meal-delivery
		[MEAL_DELIVERY_PATH]: GridLayoutBeta,
		// http://local.favy.com/meat-delivery-services
		[MEAT_DELIVERY_SERVICES_PATH]: FavyColumns,
		// http://local.favy.com/mushroom-coffee/
		[MUSHROOM_COFFEE_PATH]: GridLayoutBeta,
		// http://local.favy.com/pdf-editors
		[PDF_EDITORS_PATH]: FavySoftwareWebWidget,
		// http://local.favy.com/personal-vpn
		[PERSONAL_VPN_PATH]: FavySoftwareWebWidget,
		// http://local.favy.com/pet-food-delivery
		[PET_FOOD_DELIVERY_PATH]: FavyColumnsCoupon,
		// http://local.favy.com/promotional-products
		[PROMOTIONAL_PRODUCTS_PATH]: FavyColumnsWebWidget,
		// http://local.favy.com/solar
		[SOLAR_PATH]: FavyColumnsPhone,
		// http://local.favy.com/survey-sites
		[SURVEY_SITES_PATH]: FavyColumnsTrustpilot,
		// http://local.favy.com/tea-clubs
		[TEA_CLUBS_PATH]: FavyColumnsWebWidget,
		// http://local.favy.com/teeth-whitening
		[TEETH_WHITENING_PATH]: GridLayoutBeta,
		// http://local.favy.com/website-builders
		[WEBSITE_BUILDERS_PATH]: FavySoftwareWebWidget,
		// http://local.favy.com/weight-loss
		[WEIGHT_LOSS_PATH]: WeightLossCompany,
		// http://local.favy.com/wine-clubs
		[WINE_CLUBS_PATH]: GridLayoutBeta,
	};

	const Component = componentMap[vertical];
	if (!Component) {
		logError(`Missing component for vertical: ${vertical}`);
		return <GridLayoutBeta />;
	}

	return <Component />;
};

export const App = () => (
	<AppProviders>
		<BaseVerticalCheck vertical={HAIR_GROWTH_PATH}>
			<HairGrowthFilter />
		</BaseVerticalCheck>

		<BaseTransitionWrapper>
			<BaseCompanyList
				styles={styles}
				mainContainerSlot={
					<>
						<BaseVerticalCheck vertical={BACKGROUND_SEARCH_PATH}>
							<BackgroundSearchWidget />
						</BaseVerticalCheck>

						<SaleHighlight />

						<BaseVerticalCheck vertical={INCORPORATION_SERVICES_PATH}>
							<IncorporationServicesFoldPlacements />
						</BaseVerticalCheck>

						<CompanyListHeader />
					</>
				}
			>
				<FavyCompany />
			</BaseCompanyList>
		</BaseTransitionWrapper>
	</AppProviders>
);
